import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  Play,
  Pause,
  XCircle,
  RotateCcw,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  FileSpreadsheet,
  RefreshCw,
  Layers,
  User,
  Hash
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { BulkJob, BulkJobItem, BulkJobStatus } from '../../lib/jobs/types';
import { useModalLayer } from '../../lib/modalStack';

interface JobDetailModalProps {
  jobId: string | null;
  initialJob?: BulkJob | null;
  onClose: () => void;
  onJobUpdated?: () => void;
}

export const JobDetailModal: React.FC<JobDetailModalProps> = ({
  jobId,
  initialJob = null,
  onClose,
  onJobUpdated,
}) => {
  const [job, setJob] = useState<BulkJob | null>(initialJob);
  const [jobItems, setJobItems] = useState<BulkJobItem[]>([]);
  const [loading, setLoading] = useState<boolean>(!initialJob);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [itemsLoading, setItemsLoading] = useState<boolean>(true);

  const activeJobId = jobId || initialJob?.id || null;
  const zIndex = useModalLayer(!!activeJobId, onClose);

  const fetchJobDetails = useCallback(async () => {
    if (!activeJobId) return;
    try {
      const res = await safeFetch(`/api/admin/bulk-jobs/${activeJobId}`);
      if (res && res.job) {
        setJob(res.job);
      }
    } catch (err) {
      console.error('Failed to fetch job detail:', err);
    } finally {
      setLoading(false);
    }
  }, [activeJobId]);

  const fetchJobItems = useCallback(async () => {
    if (!activeJobId) return;
    try {
      setItemsLoading(true);
      const res = await safeFetch(`/api/admin/bulk-jobs/${activeJobId}/items`);
      if (res && res.items) {
        setJobItems(res.items);
      }
    } catch (err) {
      console.error('Failed to fetch job items:', err);
    } finally {
      setItemsLoading(false);
    }
  }, [activeJobId]);

  useEffect(() => {
    if (activeJobId) {
      fetchJobDetails();
      fetchJobItems();
      const interval = setInterval(() => {
        fetchJobDetails();
        fetchJobItems();
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [activeJobId, fetchJobDetails, fetchJobItems]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!activeJobId) return null;

  const handleAction = async (action: 'pause' | 'resume' | 'cancel' | 'retry-failed') => {
    setActionLoading(true);
    try {
      await safeFetch(`/api/admin/bulk-jobs/${activeJobId}/${action}`, { method: 'POST' });
      await fetchJobDetails();
      await fetchJobItems();
      if (onJobUpdated) onJobUpdated();
    } catch (err) {
      console.error(`Failed to execute ${action} on job:`, err);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status?: BulkJobStatus) => {
    if (!status) return null;
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5" /> Completed
          </span>
        );
      case 'completed_with_errors':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
            <AlertTriangle className="w-3.5 h-3.5" /> Partial Success
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> In Progress
          </span>
        );
      case 'paused':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-200 text-slate-700 border border-slate-300">
            <Pause className="w-3.5 h-3.5" /> Paused
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
            <XCircle className="w-3.5 h-3.5" /> Failed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
            <XCircle className="w-3.5 h-3.5" /> Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-200">
            <Clock className="w-3.5 h-3.5" /> Queued
          </span>
        );
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-xs animate-fade-in overflow-y-auto"
      style={{ zIndex }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 flex flex-col max-h-[90vh] overflow-hidden">
        {/* Modal Top Bar */}
        <div className="px-6 py-4 bg-white text-slate-900 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-600 flex items-center justify-center text-white shrink-0 shadow-sm">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold capitalize tracking-tight">
                  {job ? job.type.replace(/_/g, ' ') : 'Job Details'}
                </h2>
                {job && getStatusBadge(job.status)}
              </div>
              <p className="text-xs text-slate-500 font-mono mt-0.5">
                ID: {activeJobId} {job?.idempotencyKey ? `| Key: ${job.idempotencyKey}` : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                fetchJobDetails();
                fetchJobItems();
              }}
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              title="Refresh Details"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              aria-label="Close Modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        {loading && !job ? (
          <div className="p-12 text-center text-slate-500 text-sm flex flex-col items-center justify-center flex-1">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-3" />
            Loading job details...
          </div>
        ) : !job ? (
          <div className="p-12 text-center text-rose-500 text-sm flex flex-col items-center justify-center flex-1">
            <AlertTriangle className="w-8 h-8 mb-2" />
            Job metadata not found or failed to load.
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-y-auto">
            {/* Action Bar & Progress */}
            <div className="p-5 border-b border-slate-200 bg-slate-50/60 flex flex-wrap items-center justify-between gap-4">
              <div className="flex-1 min-w-[240px]">
                <div className="flex justify-between text-xs text-slate-700 font-medium mb-1.5">
                  <span>Execution Progress</span>
                  <span className="font-bold text-slate-900">
                    {job.progressPercent}% ({job.completedItems + job.failedItems + job.skippedItems}/{job.totalItems} items)
                  </span>
                </div>
                <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      job.status === 'completed'
                        ? 'bg-emerald-500'
                        : job.status === 'failed'
                        ? 'bg-rose-500'
                        : job.status === 'paused'
                        ? 'bg-slate-500'
                        : 'bg-blue-600'
                    }`}
                    style={{ width: `${job.progressPercent}%` }}
                  />
                </div>
              </div>

              {/* Control Action Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                {job.status === 'processing' && (
                  <button
                    onClick={() => handleAction('pause')}
                    disabled={actionLoading}
                    className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <Pause className="w-3.5 h-3.5" /> Pause
                  </button>
                )}

                {job.status === 'paused' && (
                  <button
                    onClick={() => handleAction('resume')}
                    disabled={actionLoading}
                    className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <Play className="w-3.5 h-3.5" /> Resume
                  </button>
                )}

                {(job.status === 'queued' || job.status === 'processing' || job.status === 'paused') && (
                  <button
                    onClick={() => handleAction('cancel')}
                    disabled={actionLoading}
                    className="px-3.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Cancel Job
                  </button>
                )}

                {(job.failedItems > 0 || job.status === 'failed') && (
                  <button
                    onClick={() => handleAction('retry-failed')}
                    disabled={actionLoading}
                    className="px-3.5 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Retry Failed Items
                  </button>
                )}

                <a
                  href={`/api/admin/bulk-jobs/${job.id}/report`}
                  download
                  className="px-3.5 py-1.5 bg-brand hover:bg-brand-active text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" /> Export Report CSV
                </a>
              </div>
            </div>

            {/* Metric Cards & Metadata Header */}
            <div className="p-5 border-b border-slate-100 bg-white space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-center">
                  <span className="text-[11px] font-semibold text-slate-500 block uppercase tracking-wider">Total</span>
                  <span className="text-xl font-bold text-slate-900">{job.totalItems}</span>
                </div>
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                  <span className="text-[11px] font-semibold text-emerald-700 block uppercase tracking-wider">Completed</span>
                  <span className="text-xl font-bold text-emerald-700">{job.completedItems}</span>
                </div>
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-center">
                  <span className="text-[11px] font-semibold text-rose-700 block uppercase tracking-wider">Failed</span>
                  <span className="text-xl font-bold text-rose-700">{job.failedItems}</span>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-center">
                  <span className="text-[11px] font-semibold text-amber-700 block uppercase tracking-wider">Skipped</span>
                  <span className="text-xl font-bold text-amber-700">{job.skippedItems}</span>
                </div>
                <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl text-center">
                  <span className="text-[11px] font-semibold text-sky-700 block uppercase tracking-wider">Queued/Active</span>
                  <span className="text-xl font-bold text-sky-700">{job.queuedItems + job.processingItems}</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between text-xs text-slate-500 pt-1 border-t border-slate-100 gap-2">
                <div className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-slate-400" />
                  <span>Initiated by: <strong className="text-slate-700">{job.createdByName || job.createdByEmail || 'Admin User'}</strong></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span>Created: <strong className="text-slate-700">{new Date(job.createdAt).toLocaleString()}</strong></span>
                </div>
                {job.completedAt && (
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    <span>Finished: <strong className="text-slate-700">{new Date(job.completedAt).toLocaleString()}</strong></span>
                  </div>
                )}
              </div>
            </div>

            {/* Items Table Section */}
            <div className="flex-1 p-5 overflow-y-auto min-h-[260px] max-h-[400px]">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-bold text-xs text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <Hash className="w-4 h-4 text-blue-600" /> Item Level Audit Log ({jobItems.length})
                </h4>
                {itemsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
              </div>

              {jobItems.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-xs border border-dashed border-slate-200 rounded-xl">
                  No individual item records found for this job.
                </div>
              ) : (
                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                  <table className="w-full text-left text-xs text-slate-700">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase text-[10px] font-bold tracking-wider">
                      <tr>
                        <th className="p-3">Item ID</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Target / Entity</th>
                        <th className="p-3 text-center">Attempts</th>
                        <th className="p-3">Error Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-mono text-[11px]">
                      {jobItems.map((item) => (
                        <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="p-3 font-semibold text-slate-900">{item.id}</td>
                          <td className="p-3 font-sans">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                                item.status === 'completed'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : item.status === 'failed'
                                  ? 'bg-rose-100 text-rose-800'
                                  : item.status === 'skipped'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td className="p-3 font-sans text-slate-800">
                            {item.recipientEmail || item.entityId || item.applicationId || 'N/A'}
                          </td>
                          <td className="p-3 text-center">{item.attempts} / {item.maxAttempts}</td>
                          <td className="p-3 text-rose-600 font-sans max-w-xs truncate">
                            {item.error?.message || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
